import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { messageQueries, THREAD_MESSAGE_PAGE_SIZE } from "@/shared/messageQueries.ts";
import { openQuestions } from "@/shared/openQuestions.ts";

/**
 * How many questions okthx is waiting on for a task, and why it is asking.
 *
 * The task itself carries only `needsInput`, a boolean, so saying anything
 * specific means reading the thread. One task's page is open at a time, so
 * that is one request.
 *
 * Straight off `messageQueries`, not through `useMessageList`: that hook is
 * built for a thread someone is reading and talking in — it opens a session
 * socket, carries the composer's send and wants the canvas's snapshot — and a
 * page that wants to count questions needs none of it. It also fetches for
 * `threadId: 0`, which the server rejects; a task with no thread must fetch
 * nothing, so the query is disabled rather than pointed at 0.
 *
 * The phone's `useOpenQuestions` answers the same question from the same
 * shared reader; only the fetch differs.
 */
export const useOpenQuestions = (threadId: number | null | undefined) => {
  const { data } = useInfiniteQuery({
    ...messageQueries.list(threadId ?? 0, THREAD_MESSAGE_PAGE_SIZE),
    enabled: !!threadId,
  });

  return useMemo(() => {
    const items = threadId ? (data?.pages.flatMap((page) => page.items) ?? []) : [];
    return openQuestions(items);
  }, [data, threadId]);
};
